import React, { FC } from 'react';
import { Controller, ControllerRenderProps, UseFormGetValues, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { Text } from '@fiverr-private/typography';
import { logger } from '@fiverr-private/obs';
import { ComboBox, FormControl } from '@fiverr-private/orca';
import { translate as t } from '@fiverr-private/i18n-react';
import { companiesAutocomplete } from '../../../../apis/workExperience';
import { CompanyProfile, CompanyStatus } from '../../../../gql-operations/gql-generated';
import { T_FORM_KEY } from '../../utils';
import { FieldComponentProps, WorkExperienceFormType } from '../../shared/types';

import { comboBoxError, comboBoxStyle } from '../../shared/styles.ve.css';

const mapCompaniesAndRemoveDuplicates = (companies: Partial<CompanyProfile>[]) =>
    companies
        .map((company) => ({
            label: company.name,
            value: company?.id,
            ...company,
        }))
        .filter(
            (company, index, self) =>
                index === self.findIndex((existingCompany) => existingCompany.label === company.label)
        );

interface CompanyFieldProps extends FieldComponentProps {
    getValues: UseFormGetValues<WorkExperienceFormType>;
    trigger: UseFormTrigger<WorkExperienceFormType>;
    setValue: UseFormSetValue<WorkExperienceFormType>;
}

const handleTextChange = (text: string, callback: (companySuggestions: Partial<CompanyProfile>[]) => void) => {
    companiesAutocomplete(text?.toLowerCase())
        .then((companiesResponse) => callback(mapCompaniesAndRemoveDuplicates(companiesResponse?.suggestions || [])))
        .catch((error) => logger.error('Error in companiesAutocomplete', error));
};

const debouncedCompaniesAutocomplete = debounce(handleTextChange, 500, { leading: true, maxWait: 1000 });

const CompanyField: FC<CompanyFieldProps> = ({ control, errors, trigger, setValue, getValues }) => {
    const enrichNewCompany = (company: { label: string; value: string; __isNew__?: boolean }): CompanyProfile => {
        const industry = getValues('industry');

        return {
            description: '',
            status: CompanyStatus.Unverified,
            logo: null,
            id: '',
            name: company.label,
            inFortuneList: false,
            industry,
        };
    };

    const handleCompanyChange = async (
        selectedCompany,
        onChange: ControllerRenderProps<WorkExperienceFormType>['onChange']
    ) => {
        trigger('industry');

        if (selectedCompany?.__isNew__) {
            const enrichedNewCompanyProfile = enrichNewCompany(selectedCompany);
            return onChange(enrichedNewCompanyProfile);
        }

        if (selectedCompany?.industry) {
            setValue('industry', selectedCompany?.industry);
        }

        return onChange({ ...selectedCompany, status: CompanyStatus.Approved });
    };

    return (
        <FormControl label={t(`${T_FORM_KEY}.fields.company.label`)}>
            <Controller
                name="company"
                control={control}
                rules={{
                    required: { value: true, message: t(`${T_FORM_KEY}.fields.company.required_error`) },
                    validate: (company) => {
                        if (company?.name && company.name.length < 3) {
                            return t(`${T_FORM_KEY}.minimum_letters_error`);
                        }
                    },
                }}
                render={({ field: { onChange, value: company, name } }) => (
                    <ComboBox
                        menuShouldBlockScroll
                        id={name}
                        ChevronComponent={() => <></>}
                        className={classNames(comboBoxStyle, { [comboBoxError]: errors?.company })}
                        placeholder={t(`${T_FORM_KEY}.fields.company.placeholder`)}
                        isMulti={false}
                        isCreatable
                        isAsync
                        loadOptions={debouncedCompaniesAutocomplete}
                        onChange={(selectedCompany) => handleCompanyChange(selectedCompany, onChange)}
                        noOptionsHide
                        value={company && { label: company.name, value: company.id }}
                        formatCreateLabel={(inputValue) => inputValue}
                    />
                )}
            />
            <Text color="validationError" marginTop="2" hidden={!errors?.company}>
                {errors?.company?.message}
            </Text>
        </FormControl>
    );
};

export default CompanyField;
